import React, { useCallback, useEffect, useState } from 'react'
import { IAddAttachmentsRPTestReportModel } from '../../add-new-certificate/rp-library/shared/interfaces/IAddAttachmentsRPTestReportModel'
import { IPaginatedRPDocumentAttachments } from '../../add-new-certificate/rp-library/shared/interfaces/IAddAttachmentsRPTestReportModel'
import { CertificateDocumentStatus } from '../../../../../enums/CertificateDocumentStatus'
import EcmIntegrationService from '../../../../../services/EcmIntegrationService'
import saveAs from 'file-saver'
import CertificateListService from '../../../../../services/CertificateListService'
import classes from './CertificateDetailsRPAttachmentsV2.module.scss'
import useToastr from '../../../../../hooks/useToastr'
import { Tree, TreeProps } from 'primereact/tree'
import TreeNode from 'primereact/treenode'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCaretDown,
  faCaretRight,
  faLink,
  faLinkSlash,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import CertificateDetailsRPStatusIcon from '../certificate-details-rp-attachments/certificate-details-rp-status-icon/CertificateDetailsRPStatusIcon'
import clsx from 'clsx'
import CertificateDetailsRPLinkButton from '../certificate-details-rp-attachments/certificate-details-rp-link-button/CertificateDetailsRPLinkButton'
import { AddAttachmentsRPUtils } from '../../add-new-certificate/rp-library/shared/utils/AddAttachmentsRPUtils'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  displayLinkEcmModalHeader,
  displayUnlinkEcmModalHeader,
  LinkToWbAndEcmBtnText,
  LinkToWbBtnText,
  RPAttachmentLinkButtonEnum,
  UnlinkEcmBtnText,
} from '../../add-new-certificate/rp-library/shared/constants/AddAttachmentsRPConstants'
import ConfirmationModal from '../../../../../shared/confirmation-modal/ConfirmationModal'
import {
  ecmUnavailableHeader,
  ecmUnavailableMessage,
} from '../../../../../utils/constants/ecm.constants'
import { appInsights } from '../../../../../services/appInsights'
import { v4 as uuidv4 } from 'uuid'
import { SeverityLevel } from '@microsoft/applicationinsights-web'
import { IAddAttachmentsRPTestSupportingDocumentModel } from '../../add-new-certificate/rp-library/shared/interfaces/IAddAttachmentsRPTestSupportingDocumentModel'
import { useSignalR } from '../../../../../hooks/useSignalR'

import {
  AttachmentUploadingErrorHeader,
  AttachmentUploadingErrorToastMessage,
} from './../shared/constants/CertificateUploadToastMessages'

interface CertificateDetailsRPAttachmentsV2Props {
  rpAttachments: IPaginatedRPDocumentAttachments | null
  setRPAttachments: React.Dispatch<React.SetStateAction<IPaginatedRPDocumentAttachments | null>>
  details: any
  hasCertificateBeenActive: boolean
  reloadAttachments: () => Promise<void>
  reloadAttachmentsV2: (pageNumber?: number, pageCount?: number) => Promise<void>
  getNextPage: (pageNumber: number, pageCount?: number) => Promise<void>
  isFetchingData: boolean
}

interface CustomTreeProps extends TreeProps {
  parent?: any | null
}

interface RPActionLinkProps {
  label: string
  icon: IconProp
  buttonType: RPAttachmentLinkButtonEnum
  onClick: () => void | (() => Promise<void>)
}

interface ConfirmationModalProps {
  rpAttachmentData: IAddAttachmentsRPTestReportModel | null
  onConfirm: () => void
  onCancel: () => void
  visible: boolean
  headerDisplay: string
  buttonYesDisplay: string
  buttonNoDisplay: string
  confirmationMessageHTML: Element | JSX.Element | string
  style?: object | undefined
  className?: string
  isSaving?: boolean
}

const initialConfirmationModalValues: ConfirmationModalProps = {
  rpAttachmentData: null,
  onConfirm: () => {},
  onCancel: () => {},
  visible: false,
  headerDisplay: '',
  buttonNoDisplay: 'No',
  buttonYesDisplay: 'Yes',
  confirmationMessageHTML: '',
  style: undefined,
  isSaving: undefined,
}

interface CertificateLinkWbAndEcmRequestModel {
  certificateId: string
  certificateDocumentId: string
  ecmFileNodeId: string
  ipCertificateDetailsModel: any
}

interface IEcmCertificateDocument {
  certificateDocumentId: string
  ecmFileNodeId: string
  isTestReportDuplicate: boolean
  iPCertificateDetailsModel: any
  workbenchCertificateId: string
}

const logPrefix = `CertificateDetailsRPAttachmentsV2 - ${uuidv4()}`

const supportedStatuses = [
  CertificateDocumentStatus.LinkedToWbAndEcmTic,
  CertificateDocumentStatus.UploadToEcmFailed,
  CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic,
  CertificateDocumentStatus.TestReportDuplicate,
  CertificateDocumentStatus.TestReportDuplicateLinkToEcmFailed,
  CertificateDocumentStatus.SavedInEcmTic,
]

const CertificateDetailsRPAttachmentsV2 = (props: CertificateDetailsRPAttachmentsV2Props) => {
  const { showError, showWarning, showInfo, showSuccess } = useToastr()

  const [confirmationModalProps, setConfirmationModalProps] = useState<ConfirmationModalProps>(
    initialConfirmationModalValues
  )

  const isCertificateUnderRevision = props.details?.certificateStatus === 'Under Revision'

  const hubUrl = process.env.REACT_APP_GMAE2E_FUNC_URL + '/api'
  const { connection: signalRConnection } = useSignalR(hubUrl, true)

  const currentRPAttachments = props.rpAttachments?.records ?? []

  const handleCertificateRPSignalRFlyout = useCallback(
    (isSuccess, result, shouldShowError) => {
      if (!result) return

      props.reloadAttachments()
      props.reloadAttachmentsV2(props.rpAttachments?.pageNumber)

      if (isSuccess === false && shouldShowError) {
        showWarning(AttachmentUploadingErrorHeader, AttachmentUploadingErrorToastMessage)
      }
    },
    [props, showWarning]
  )

  useEffect(() => {
    if (signalRConnection) {
      signalRConnection.on('ecm-uploading-certificate-rp-flyout-success', result => {
        handleCertificateRPSignalRFlyout(true, result, false)
      })
      signalRConnection.on('ecm-uploading-certificate-rp-success', result => {
        handleCertificateRPSignalRFlyout(true, result, false)
      })
      signalRConnection.on('ecm-uploading-certificate-rp-failed', result => {
        handleCertificateRPSignalRFlyout(false, result, false)
      })
      signalRConnection.on('ecm-uploading-certificate-rp-attachment-success', result => {
        handleCertificateRPSignalRFlyout(true, result, false)
      })
      signalRConnection.on('ecm-uploading-certificate-rp-attachment-failed', result => {
        handleCertificateRPSignalRFlyout(false, result, false)
      })
      signalRConnection.on('ecm-uploading-certificate-rp-pendingdocument-success', result => {
        handleCertificateRPSignalRFlyout(true, result, false)
      })
      signalRConnection.on('ecm-uploading-certificate-rp-pendingdocument-failed', result => {
        handleCertificateRPSignalRFlyout(false, result, false)
      })
      signalRConnection.on('ecm-uploading-certificate-rp-flyout-failed', result => {
        handleCertificateRPSignalRFlyout(false, result, true)
      })
    }

    return () => {
      if (signalRConnection) {
        signalRConnection.off('ecm-uploading-certificate-rp-flyout-success')
        signalRConnection.off('ecm-uploading-certificate-rp-flyout-failed')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalRConnection, handleCertificateRPSignalRFlyout])

  const downloadFile = async (
    file: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel
  ) => {
    switch (file.status) {
      case CertificateDocumentStatus.LinkedToWbAndEcmTic:
      case CertificateDocumentStatus.SavedInEcmTic:
        await EcmIntegrationService.downloadEcmFile(file.ecmFileNodeId ?? '').then(response => {
          if (response) {
            const fileName = file.ecmFileNodeId
              ? (file.ecmFileName ?? '')
              : (file.documentFileName ?? file.ecmFileName)
            saveAs(response, fileName)
          } else {
            showError(
              'Unable to Download File',
              'There is a problem while connecting to ECM Folder. Please try again later.'
            )
          }
        })
        break
      case CertificateDocumentStatus.UploadToEcmFailed:
      case CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic:
      case CertificateDocumentStatus.TestReportDuplicateLinkToEcmFailed:
        await CertificateListService.downloadCertificateDocument(file.id).then(response => {
          if (response) {
            saveAs(response, file.documentFileName)
          } else {
            showError('Error!', 'File does not exists.')
          }
        })
        break
    }
  }

  const transformRPDataToTreeData = (): TreeNode[] => {
    return currentRPAttachments.map((rpAttachment: any) => {
      const treeItem: TreeNode = {
        key: rpAttachment.id,
        label:
          (rpAttachment.status === CertificateDocumentStatus.LinkedToWbAndEcmTic ||
            rpAttachment.status === CertificateDocumentStatus.SavedInEcmTic ||
            rpAttachment.status === CertificateDocumentStatus.UploadToEcmInProgress ||
            rpAttachment.status === CertificateDocumentStatus.UnlinkingInProgress) &&
          rpAttachment.ecmFileNodeId
            ? (rpAttachment.ecmFileName ?? '')
            : rpAttachment.documentFileName,
        data: rpAttachment,
        children:
          rpAttachment.supportingDocuments.length === 0
            ? [
                {
                  key: `${rpAttachment.id}-empty`,
                  data: rpAttachment,
                  label: 'No Supporting Documents available.',
                },
              ]
            : rpAttachment.supportingDocuments?.map((suppDoc: any) => {
                const childTreeItem: TreeNode = {
                  key: suppDoc.id,
                  label:
                    (suppDoc.status === CertificateDocumentStatus.LinkedToWbAndEcmTic ||
                      suppDoc.status === CertificateDocumentStatus.SavedInEcmTic ||
                      suppDoc.status === CertificateDocumentStatus.UploadToEcmInProgress) &&
                    suppDoc.ecmFileNodeId
                      ? (suppDoc.ecmFileName ?? '')
                      : suppDoc.documentFileName,
                  data: suppDoc,
                }

                return childTreeItem
              }),
      }
      return treeItem
    })
  }

  const onWbLinkOrUnlinkError = (
    error: any,
    functionName: 'handleLinkToWb' | 'handleUnlinkEcm'
  ) => {
    appInsights?.trackTrace({
      message: `${logPrefix} - ${functionName} - ${JSON.stringify(error)}`,
      severityLevel: SeverityLevel.Error,
    })

    showWarning(ecmUnavailableHeader, ecmUnavailableMessage)
  }

  const updateAttachmentsInProgress = (
    rpAttachment: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel,
    status: CertificateDocumentStatus
  ) => {
    const cloneCurrentRPAttachments = structuredClone(currentRPAttachments).map(
      (currentRPAttachment: any) => {
        if (currentRPAttachment.id === rpAttachment.id) currentRPAttachment.status = status

        currentRPAttachment.supportingDocuments?.map((currentSuppDoc: any) => {
          if (currentSuppDoc.id === rpAttachment.id) currentSuppDoc.status = status

          return currentSuppDoc
        })

        return currentRPAttachment
      }
    )

    props.setRPAttachments((current: any) =>
      current
        ? {
            ...current,
            records: cloneCurrentRPAttachments,
          }
        : current
    )
  }

  //#region Link, Relink, Unlink, Delete Attachment Methods

  const handleRelinkLinkEcm = async (
    rpAttachment: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel,
    isModalShown: boolean
  ) => {
    if (!rpAttachment?.id) return showError('Failed!', 'Attachment has no valid id.')

    if (isModalShown)
      setConfirmationModalProps(current => ({
        ...current,
        isSaving: true,
      }))

    const cloneCurrentRPAttachments = structuredClone(rpAttachment)
    cloneCurrentRPAttachments.status = CertificateDocumentStatus.UploadToEcmInProgress

    updateAttachmentsInProgress(
      cloneCurrentRPAttachments,
      CertificateDocumentStatus.UploadToEcmInProgress
    )

    const isFileNewlyAdded = !rpAttachment.ecmFileNodeId

    const singleLinkRequestPayload: CertificateLinkWbAndEcmRequestModel = {
      certificateId: props.details.certificateId,
      certificateDocumentId: rpAttachment.id,
      ecmFileNodeId: rpAttachment.ecmFileNodeId ?? '',
      ipCertificateDetailsModel: props.details,
    }

    CertificateListService.certificateAttachmentSingleLinkUploadECM(singleLinkRequestPayload)
      .then(response => {
        if (response) {
          if (response.isSuccess) {
            if (isFileNewlyAdded) {
              showSuccess(
                'Success!',
                'Uploading of document(s) has been successfully processed. Please refer to Attachments Section of the certificate to view the status of the document(s).'
              )

              props.reloadAttachments()
              props.reloadAttachmentsV2(props.rpAttachments?.pageNumber)
            } else {
              showInfo(
                'Document Upload Ongoing',
                'The document(s) you added is now being uploaded to Workbench and ECM TIC. You will receive a notification once completed.'
              )
            }
          } else {
            onWbLinkOrUnlinkError(response, 'handleLinkToWb')
          }
        } else {
          onWbLinkOrUnlinkError(response, 'handleLinkToWb')
        }
      })
      .catch(() => showWarning(ecmUnavailableHeader, ecmUnavailableMessage))
      .finally(() => {
        setConfirmationModalProps(initialConfirmationModalValues)
      })
  }

  const handleUnlinkEcm = async (
    rpAttachment: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel
  ) => {
    if (!rpAttachment?.ecmFileNodeId || !rpAttachment?.id)
      return showError('Failed!', 'Attachment has no valid id.')

    setConfirmationModalProps(current => ({
      ...current,
      isSaving: true,
    }))

    updateAttachmentsInProgress(rpAttachment, CertificateDocumentStatus.UploadToEcmInProgress)

    let payload: IEcmCertificateDocument = {
      certificateDocumentId: rpAttachment?.id,
      ecmFileNodeId: rpAttachment?.ecmFileNodeId,
      isTestReportDuplicate: false,
      workbenchCertificateId: props.details.certficiateId,
      iPCertificateDetailsModel: props.details,
    }

    await CertificateListService.unlinkEcmDocument(payload)
      .then(response => {
        if (response?.isSuccess) {
          showSuccess('Success!', 'The document(s) has been unlinked from Workbench successfully')
          setConfirmationModalProps(initialConfirmationModalValues)
        } else {
          onWbLinkOrUnlinkError(response, 'handleUnlinkEcm')
          setConfirmationModalProps(initialConfirmationModalValues)
        }
      })
      .catch(error => {
        onWbLinkOrUnlinkError(error, 'handleUnlinkEcm')
        setConfirmationModalProps(initialConfirmationModalValues)
      })

    await props.reloadAttachments()
    await props.reloadAttachmentsV2(props.rpAttachments?.pageNumber)
  }

  const handleLinkToWb = async (
    rpAttachment: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel,
    isTestReportDuplicate: boolean = false
  ) => {
    const cloneCurrentRPAttachment = structuredClone(rpAttachment)
    cloneCurrentRPAttachment.status = CertificateDocumentStatus.UploadToEcmInProgress

    updateAttachmentsInProgress(
      cloneCurrentRPAttachment,
      CertificateDocumentStatus.UploadToEcmInProgress
    )

    let payload: IEcmCertificateDocument = {
      certificateDocumentId: rpAttachment?.id,
      ecmFileNodeId: rpAttachment?.ecmFileNodeId ?? '',
      isTestReportDuplicate: isTestReportDuplicate,
      iPCertificateDetailsModel: props.details,
      workbenchCertificateId: props.details.certificateId,
    }

    await CertificateListService.linkWb(payload)
      .then(response => {
        if (response) {
          if (response.isSuccess) {
            showSuccess('Success!', 'The document(s) has been uploaded to Workbench successfully.')
          } else {
            onWbLinkOrUnlinkError(response, 'handleLinkToWb')
          }
        } else {
          onWbLinkOrUnlinkError(response, 'handleLinkToWb')
        }
      })
      .catch(error => onWbLinkOrUnlinkError(error, 'handleLinkToWb'))

    await props.reloadAttachments()
    await props.reloadAttachmentsV2(props.rpAttachments?.pageNumber)
  }

  const handleDeleteFileToBlobStorage = async (
    rpAttachment: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel
  ) => {
    setConfirmationModalProps(current => ({
      ...current,
      isSaving: true,
    }))

    CertificateListService.deleteFileFromBlob(rpAttachment.id)
      .then(response => {
        if (!response?.isSuccess) {
          setConfirmationModalProps(initialConfirmationModalValues)
          showError('Error!', 'Attachment(s) has not been deleted.')
          return
        }

        props.reloadAttachments()
        props.reloadAttachmentsV2(props.rpAttachments?.pageNumber)
        showSuccess(
          'Success!',
          'Attachment(s) has been deleted successfully from GMAWB File Storage.'
        )
        setConfirmationModalProps(initialConfirmationModalValues)
      })
      .catch(() => {
        setConfirmationModalProps(initialConfirmationModalValues)
        showError('Error!', 'Attachment(s) has not been deleted')
      })
  }

  //#endregion

  const deleteConfirmationMessage = () => {
    return (
      <span>
        The document(s) will be deleted from GMAWB File Storage.
        <br />
        <br />
        Would you like to continue?
      </span>
    )
  }

  const displayUnlinkEcmModalText = () => {
    return (
      <span>
        This change will not affect the document in ECM TIC.
        <br />
        <br />
        Are you sure you want to unlink the selected Document(s)?
      </span>
    )
  }
  const displayLinkEcmModalText = () => {
    return (
      <span>
        File(s) will be saved to ECM TIC and cannot be deleted.
        <br />
        <br />
        Would you like to continue?
      </span>
    )
  }

  const handleUnlinkConfirmationModalProps = (
    rpAttachment: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel
  ) => {
    setConfirmationModalProps(current => {
      return {
        ...current,
        headerDisplay: displayUnlinkEcmModalHeader,
        confirmationMessageHTML: displayUnlinkEcmModalText(),
        visible: true,
        onCancel: () => {
          setConfirmationModalProps(initialConfirmationModalValues)
        },
        onConfirm: () => {
          handleUnlinkEcm(rpAttachment)
        },
      }
    })
  }

  const handleLinkConfirmationProps = (
    rpAttachment: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel
  ) => {
    setConfirmationModalProps(current => {
      return {
        ...current,
        headerDisplay: displayLinkEcmModalHeader,
        confirmationMessageHTML: displayLinkEcmModalText(),
        visible: true,
        onCancel: () => {
          setConfirmationModalProps(initialConfirmationModalValues)
        },
        onConfirm: () => {
          handleRelinkLinkEcm(rpAttachment, true)
        },
      }
    })
  }

  const actionLinkProps = (
    rpAttachment: IAddAttachmentsRPTestReportModel & IAddAttachmentsRPTestSupportingDocumentModel
  ): RPActionLinkProps | null => {
    const isWithdrawnCertificate = props.details?.certificateStatus === 'Withdrawn'

    if (
      !supportedStatuses.includes(rpAttachment.status!) ||
      isWithdrawnCertificate ||
      (!props.hasCertificateBeenActive &&
        rpAttachment.status !== CertificateDocumentStatus.LinkedToWbAndEcmTic)
    )
      return null

    let label = UnlinkEcmBtnText
    let icon: IconProp = faLinkSlash
    let buttonType: RPAttachmentLinkButtonEnum = RPAttachmentLinkButtonEnum.unlinkEcm

    let onClick = () => handleUnlinkConfirmationModalProps(rpAttachment)

    switch (rpAttachment.status) {
      case CertificateDocumentStatus.UploadToEcmFailed: {
        onClick = () => handleRelinkLinkEcm(rpAttachment, false)
        label = LinkToWbAndEcmBtnText
        icon = faLink
        buttonType = RPAttachmentLinkButtonEnum.linkToWbAndEcm
        break
      }
      case CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic: {
        onClick = () => handleLinkConfirmationProps(rpAttachment)
        label = LinkToWbAndEcmBtnText
        icon = faLink
        buttonType = RPAttachmentLinkButtonEnum.linkToWbAndEcm
        break
      }

      case CertificateDocumentStatus.SavedInEcmTic: {
        onClick = () => handleLinkToWb(rpAttachment)
        label = LinkToWbBtnText
        icon = faLink
        buttonType = RPAttachmentLinkButtonEnum.linkToWb
        break
      }
    }

    const returnObject: RPActionLinkProps = {
      label: label,
      icon: icon,
      buttonType: buttonType,
      onClick: onClick,
    }

    return returnObject
  }

  return (
    <>
      {currentRPAttachments.length === 0 && <span>No files available.</span>}

      {currentRPAttachments.length > 0 && (
        <>
          <div className={classes['tree-shell']}>
            <Tree
              className={classes['tree-root']}
              togglerTemplate={(node, options) => {
                const hasNoChildren = !node.children || node.children?.length === 0

                return (
                  <div
                    className={clsx(classes['tree-container-caret'])}
                    style={{
                      maxWidth: '8px',
                      visibility: hasNoChildren ? 'hidden' : undefined,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={options.expanded ? faCaretDown : faCaretRight}
                      onClick={options.onClick}
                    />
                  </div>
                )
              }}
              contentClassName={classes['tree-container-content']}
              nodeTemplate={(node, options) => {
                const optionProps = options.props as CustomTreeProps
                const linkProps = actionLinkProps(node.data)
                const isLinkUnlinkInProgress =
                  currentRPAttachments.some(
                    (rpAttachment: any) =>
                      rpAttachment.id === node.data.id &&
                      AddAttachmentsRPUtils.isLinkUnlinkInProgress(rpAttachment.status)
                  ) ||
                  currentRPAttachments.some((rpAttachment: any) =>
                    rpAttachment.supportingDocuments.some(
                      (suppDoc: any) =>
                        suppDoc.id === node.data.id &&
                        AddAttachmentsRPUtils.isLinkUnlinkInProgress(suppDoc.status)
                    )
                  )

                const shouldDisplayDeleteButton = AddAttachmentsRPUtils.isAttachmentValidToDelete(
                  node.data.status
                )

                if (node.key === `${node.data.id}-empty`) {
                  return (
                    <div>
                      <div style={{ fontStyle: 'italic' }}>
                        <span title={node.label}>{node.label}</span>
                      </div>
                    </div>
                  )
                } else {
                  return (
                    <div
                      className={clsx(
                        {
                          [classes['tree-container-node']]: !optionProps.parent,
                        },
                        {
                          [classes['tree-container-child-node']]: optionProps.parent,
                        }
                      )}
                    >
                      <div
                        className={clsx(
                          'text-overflow-ellipsis',
                          {
                            [classes['tree-container-node-link']]:
                              !optionProps.parent &&
                              !AddAttachmentsRPUtils.isStatusUploading(node.data.status),
                          },
                          {
                            [classes['tree-container-child-node-link']]:
                              optionProps.parent &&
                              !AddAttachmentsRPUtils.isStatusUploading(node.data.status),
                          }
                        )}
                      >
                        <span title={node.label} onClick={() => downloadFile(node.data)}>
                          {node.label}
                        </span>
                      </div>
                      <div
                        style={{
                          paddingLeft: !optionProps.parent ? '0.2rem' : '0.5rem',
                        }}
                      >
                        {node.data?.status !== null && (
                          <CertificateDetailsRPStatusIcon
                            rpAttachmentFile={node.data}
                            isLinkInProcess={isLinkUnlinkInProgress}
                          />
                        )}
                      </div>
                      <div
                        className={clsx(
                          {
                            [classes['tree-container-node-item-link']]: !optionProps.parent,
                          },
                          {
                            [classes['tree-container-child-node-item-link']]: optionProps.parent,
                          }
                        )}
                      >
                        {props.hasCertificateBeenActive &&
                          linkProps &&
                          AddAttachmentsRPUtils.shouldShowLinkButtonFlyout(
                            node.data.contentGroup,
                            node.data.status,
                            props.details.certificateScheme,
                            node.data.attachmentType
                          ) && (
                            <CertificateDetailsRPLinkButton
                              label={linkProps.label}
                              icon={linkProps.icon}
                              linkButtonType={linkProps.buttonType}
                              onClick={linkProps.onClick}
                            />
                          )}
                      </div>

                      {shouldDisplayDeleteButton && (
                        <span
                          title="Delete Attachment"
                          onClick={() =>
                            setConfirmationModalProps(current => ({
                              ...current,
                              visible: true,
                              headerDisplay: 'Delete File Confirmation',
                              confirmationMessageHTML: deleteConfirmationMessage(),
                              onCancel: () =>
                                setConfirmationModalProps(initialConfirmationModalValues),
                              onConfirm: () => handleDeleteFileToBlobStorage(node.data),
                            }))
                          }
                        >
                          <FontAwesomeIcon className={`${classes['icons']}`} icon={faTrash} />
                        </span>
                      )}
                    </div>
                  )
                }
              }}
              value={transformRPDataToTreeData()}
            />

            {props.rpAttachments?.hasNextPage && !props.isFetchingData && (
              <div className={classes['showmore-container']}>
                <span
                  onClick={() => props.getNextPage((props.rpAttachments?.pageNumber ?? 0) + 1)}
                  className={`${classes['showmore-btn']} ${classes['downloadLink']}`}
                >
                  Show More
                </span>
              </div>
            )}
          </div>

          <ConfirmationModal {...confirmationModalProps} />
        </>
      )}
    </>
  )
}

export default CertificateDetailsRPAttachmentsV2
